import { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon"; // Keep if you use Icon directly
import MDBox from "components/MDBox"; // Keep if you use MDBox directly
import Sidenav from "examples/Sidenav";
import theme from "assets/theme";
import getAppRoutes from "routes";
import { useMaterialUIController, setMiniSidenav } from "context"; // Removed setOpenConfigurator if not used
import brandWhite from "assets/images/logo-convokko.png";
import brandDark from "assets/images/logo-convokko.png";

// Import your SignIn and LogoutComponent directly here for explicit routing if isAuthenticated is false
import SignIn from "layouts/authentication/sign-in";
import LogoutComponent from "layouts/authentication/logout/logout";
import Landing from "layouts/landing";

// La app instalada en el móvil (PWA) se abre en modo standalone. En ese caso
// la raíz debe seguir llevando al login como siempre, nunca a la landing.
const esAppInstalada = () => {
  try {
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      window.matchMedia("(display-mode: fullscreen)").matches ||
      window.navigator.standalone === true ||
      document.referrer.startsWith("android-app://")
    );
  } catch {
    return false;
  }
};

export default function App() {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, direction, layout, sidenavColor, transparentSidenav, whiteSidenav } =
    controller;

  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null: loading, false: not authenticated, true: authenticated
  const { pathname } = useLocation();

  // Verificar autenticación UNA SOLA VEZ al cargar la app
  const [routes, setRoutes] = useState([]);

  useEffect(() => {
    const checkAuth = () => {
      try {
        const token = localStorage.getItem("token");
        if (!token || token === "undefined" || token === "null" || token.length < 10) {
          console.log("No hay token válido");
          localStorage.removeItem("token");
          setIsAuthenticated(false);
          return;
        }

        console.log("Token válido encontrado");
        setIsAuthenticated(true);

        // cargar rutas ahora que existe token y rol
        const newRoutes = getAppRoutes();
        setRoutes(newRoutes);
      } catch (error) {
        console.error("Error verificando token:", error);
        localStorage.clear();
        setIsAuthenticated(false);
      }
    };
    checkAuth();
  }, []);

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Set document direction
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Scroll to top on route change
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  // Helper to generate routes from the configuration array
  const renderRoutes = (allRoutes) =>
    allRoutes.flatMap((route) => {
      if (route.collapse) {
        return renderRoutes(route.collapse);
      }
      if (route.route && !route.external) {
        return <Route key={route.key} path={route.route} element={route.component} />;
      }
      return [];
    });

  // --- Conditional Rendering based on Authentication State ---

  // 0. Landing pública: accesible con o sin sesión y sin el Sidenav.
  if (pathname === "/home") {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Landing />
      </ThemeProvider>
    );
  }

  // 1. Loading state for authentication check
  if (isAuthenticated === null) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <MDBox
          display="flex"
          flexDirection="column"
          gap={2.5}
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          sx={{ backgroundColor: "#1C2A55" }}
        >
          <MDBox
            component="img"
            src={brandWhite}
            alt="Convokko"
            width="200px"
            sx={{ filter: "brightness(0) invert(1)" }}
          />
          <CircularProgress size={28} thickness={4} sx={{ color: "#FBB53F" }} />
        </MDBox>
      </ThemeProvider>
    );
  }

  // 2. Main rendering logic: If authenticated, render main layout; otherwise, only login/logout routes
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      {isAuthenticated ? (
        // --- Authenticated User Layout ---
        <>
          {layout === "dashboard" && ( // Only show Sidenav if layout is dashboard
            <Sidenav
              color={sidenavColor}
              brand={transparentSidenav || whiteSidenav ? brandDark : brandWhite}
              brandName=""
              routes={routes} // Pass all routes to Sidenav
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
          )}

          <Routes>
            {/* Render all protected routes from your routes.js */}
            {renderRoutes(routes)}

            {/* Redirect from sign-in to dashboard if already authenticated and trying to access sign-in */}
            <Route path="/authentication/sign-in" element={<Navigate to="/dashboard" replace />} />

            {/* Catch-all for authenticated users trying to access unknown paths */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </>
      ) : (
        // --- Unauthenticated User Layout (only login/logout accessible) ---
        <Routes>
          {/* Explicitly define login and logout routes when not authenticated */}
          <Route path="/authentication/sign-in" element={<SignIn />} />
          <Route path="/logout" element={<LogoutComponent />} /> {/* Keep logout accessible */}
          {/* Raíz sin sesión: la web muestra la landing; la app instalada va al login */}
          <Route
            path="/"
            element={
              <Navigate to={esAppInstalada() ? "/authentication/sign-in" : "/home"} replace />
            }
          />
          {/* Any other path when not authenticated redirects to sign-in */}
          <Route path="*" element={<Navigate to="/authentication/sign-in" replace />} />
        </Routes>
      )}
    </ThemeProvider>
  );
}
